import { FileSize } from "@enums/fileSize";
import { SignatureModule } from "@enums/signatureModule";
import {
  convertFile,
  fileNeedsConversion,
} from "@services/Shared/converter/converterService";
import postFilesToSignService from "@services/Shared/storage/postFilesToSignService";
import putStorageService from "@services/Shared/storage/putStorageService";
import { checkSizeFile } from "@utils/helpers/gcp.helper";
import AssetSizeError from "./errors/AssetSizeError";

export enum UploadEvent {
  UPLOAD_START = "uploadStart",
  UPLOADED_BYTES = "uploadedBytes",
  UPLOADED_FILE = "uploadedFile",
  UPLOAD_SUCCESS = "uploadSuccess",
}

export interface UploadFile {
  file: File;
  name: string;
  field: string;
}

export interface UploadFileWithName {
  file: File;
  name: string;
  field: string;
}

export interface UploadedFile {
  file: File;
  name: string;
  field: string;
  url: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventListener = (...args: any[]) => void;

const SIGNED_URLS_BATCH_SIZE = 100;

/**
 * This class takes care of uploading files to GCP, along with checking that the files have the
 * expected size and that are converted into the supported media types of Buildtree
 */
class FileUploadService {
  private events: Record<UploadEvent, EventListener[]> = {
    [UploadEvent.UPLOAD_START]: [],
    [UploadEvent.UPLOADED_BYTES]: [],
    [UploadEvent.UPLOADED_FILE]: [],
    [UploadEvent.UPLOAD_SUCCESS]: [],
  };

  private readonly uuid;
  private readonly module;

  constructor(uuid: string, module: SignatureModule) {
    this.uuid = uuid;
    this.module = module;
  }

  on(event: UploadEvent, listener: EventListener) {
    this.events[event].push(listener);
    return this;
  }

  off(event: UploadEvent, listener: EventListener) {
    this.events[event] = this.events[event].filter((l) => l !== listener);
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private emit(event: UploadEvent, ...args: any[]): void {
    this.events[event].forEach((listener) => listener(...args));
  }

  async uploadFiles(
    uploadFiles: UploadFile[],
    FileSizeLimit: FileSize
  ): Promise<UploadedFile[]> {
    for (const uploadFile of uploadFiles) {
      if (checkSizeFile(uploadFile.file.size, FileSizeLimit)) {
        throw new AssetSizeError(uploadFile.file, FileSizeLimit);
      }
      if (await fileNeedsConversion(uploadFile.file)) {
        const convertedFile = await convertFile(uploadFile.file);
        uploadFile.file = convertedFile;
      }
    }

    this.emit(
      UploadEvent.UPLOAD_START,
      uploadFiles.length,
      uploadFiles.reduce((total, uploadFile) => total + uploadFile.file.size, 0)
    );

    const uploadedFiles: UploadedFile[] = [];

    const uploadFilesWithNames = await this.setFullPathNames(uploadFiles);

    let signedUrls: Record<string, string> = {};

    for (let i = 0; i < uploadFilesWithNames.length; i++) {
      const { file, name, field } = uploadFilesWithNames[i];
      if (!name) {
        throw new Error("The file name should have been set");
      }

      // Get more signed URLs
      if (Object.keys(signedUrls).length === 0) {
        signedUrls = await this.getSignedUrls(uploadFilesWithNames, i);
      }

      const signedUrl = signedUrls[name];
      delete signedUrls[name];

      if (!signedUrl) {
        throw new Error(`No signed URL for ${file.name}`);
      }

      const {
        data: { mediaLink },
      } = await putStorageService(signedUrl, file, (bytes) => {
        this.emit(UploadEvent.UPLOADED_BYTES, bytes);
      });

      const uploadedFile = { file, name, field, url: mediaLink };
      uploadedFiles.push(uploadedFile);

      this.emit(UploadEvent.UPLOADED_FILE, uploadedFile);
    }

    this.emit(UploadEvent.UPLOAD_SUCCESS, uploadFiles);

    return uploadedFiles;
  }

  private async setFullPathNames(
    uploadFiles: UploadFile[]
  ): Promise<UploadFileWithName[]> {
    const uploadFilesWithName: UploadFileWithName[] = [];

    for (const uploadFile of uploadFiles) {
      // const name = await generateNameFile(uploadFile.file, uploadFile.name);

      uploadFilesWithName.push({
        ...uploadFile,
        name: uploadFile.name
          // Remove double slashes just in case
          .replace("//", "/")
          // Remove trailing slash if exist
          .replace(/^\/+/, ""),
      });
    }

    return uploadFilesWithName;
  }

  private async getSignedUrls(
    files: UploadFileWithName[],
    index: number
  ): Promise<Record<string, string>> {
    const { data: signedUrls } = await postFilesToSignService(
      this.uuid,
      files.slice(index, index + SIGNED_URLS_BATCH_SIZE).map((f) => f.name),
      this.module
    );

    return signedUrls;
  }
}

export default FileUploadService;
